import { useForm } from "react-hook-form";
import Modal from "../../layouts/Modal";
import { IInvoice, IInvoiceItem, InvoiceStatus } from "../../models/invoice/IInvoice";
import BasketInvoiceInputs from "../../pages/Basket/BasketInvoiceInputs";
import LoadingWheel from "../shared/LoadingWheel";
import { useEffect, useMemo, useState } from "react";
import { IAccount } from "../../models/account/IAccount";
import BasketTable from "../basket/BasketTable";
import InvoiceItemsTable from "./InvoiceItemsTable";
import MultiModal from "../../layouts/MultiModal";
import MultiModalSection from "../../layouts/MultiModalSection";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { dateTimeToFormValue } from "../../utils/dateTimeToFormValue";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { get, set, update } from "lodash";
import formatDateTime from "../../utils/formatDateTime";

import useInvoices from "../../data/useInvoices";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { Button } from "@headlessui/react";
import InvoiceItemModal from "./InvoiceItemModal";
import CreditInvoiceModal from "./CreditInvoiceModal";

interface InvoiceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoice: IInvoice | null;
}

export default function InvoiceModal({ open, setOpen, invoice }: InvoiceModalProps) {
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(invoice?.account || null);
  const [invoiceItemOpen, setInvoiceItemOpen] = useState(false);
  const [closeOnSave, setCloseOnSave] = useState(true);
  const { updateInvoice } = useInvoices();
  const { saveHandlers } = useDefaultCRUDHandlers("Invoice");
  const { register, setValue, getValues, watch, reset } = useForm({
    defaultValues: {
      paymentMethod: invoice?.paymentMethod || 1,
      accountId: invoice?.accountId || "0",
      shippingPolicyId: invoice?.basket?.shippingPolicyId || "0",
      notes: invoice?.notes || "",
      postcode: invoice?.postcode || "",
      
      line1: invoice?.addressLine1 || "",
      line2: invoice?.addressLine2 || "",
      line3: invoice?.addressLine3 || "",
      line4: invoice?.addressLine4 || "",
      locality: "",
      town: invoice?.town || "",
      county: invoice?.county || "",
      
      invoiceDate: invoice?.invoiceDate || "",
      dueDate: invoice?.dueDate || "",
      reference: invoice?.reference || "",
      amountPaid: invoice?.amountPaid || 0,
      subTotal: invoice?.subTotal || 0,
      invoiceTotal: invoice?.invoiceTotal || 0,

      itemTotal: 0
    }
  });

  const itemTotal = watch("itemTotal");
  const invoiceTotal = watch("invoiceTotal");

  useEffect(() => {
    if(!invoice) return;

    console.log("Resetting form with invoice data", invoice);
    setSelectedAccount(invoice.account || null);

    reset({
      paymentMethod: invoice.paymentMethod,
      accountId: invoice.accountId || "",
      shippingPolicyId: invoice?.basket?.shippingPolicyId || "0",
      notes: invoice.notes || "",
      
      postcode: invoice?.postcode || "",
      line1: invoice.addressLine1 || "",
      line2: invoice.addressLine2 || "",
      line3: invoice.addressLine3 || "",
      line4: invoice.addressLine4 || "",
      locality: "",
      town: invoice.town || "",
      county: invoice.county || "",

      invoiceDate: dateTimeToFormValue(invoice.invoiceDate),
      dueDate: dateTimeToFormValue(invoice.dueDate),
      reference: invoice.reference,
      amountPaid: invoice.amountPaid,
      subTotal: invoice.subTotal,
      invoiceTotal: invoice.invoiceTotal,
    });
  }, [invoice]);

  useEffect(() => {
    if(closeOnSave) return;
    updateInvoiceValues();
  }, [closeOnSave]);

  const setTotalToItemTotal = () => {
    setValue("invoiceTotal", itemTotal);
    setCloseOnSave(false);
  }

  const updateInvoiceValues = () => {
    const formData = getValues();

    updateInvoice.mutate({
      id: invoice?.id || "",
      payload:{ 
        ...formData, 
        accountId: getValues("accountId") || invoice?.accountId 
      }
    }, {
      onSuccess: () => {
        console.log("Invoice updated successfully");
        saveHandlers.onSuccess();
        if (closeOnSave) setOpen(false);
        else setCloseOnSave(true);
      }
    });
  }

  const [creditModalOpen, setCreditModalOpen] = useState(false);

  const isEmpty = useMemo(() => {
    return invoice?.items && invoice.items.length === 0;
  }, [invoice]);

  if (!invoice) (<Modal open={open} setOpen={setOpen} width="sm:max-w-2xl"><LoadingWheel /></Modal>);

  return (
    <>
      <CreditInvoiceModal open={creditModalOpen} setOpen={setCreditModalOpen} invoice={invoice} />
      <MultiModal isOpen={open} onClose={setOpen}>
        <InvoiceItemModal open={invoiceItemOpen} setOpen={setInvoiceItemOpen} invoice={invoice} />
        <div className="flex flex-col gap-10">    
          <MultiModalSection classes="sm:max-w-3xl flex-grow" title="Invoice Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DateTimeInput
                label="Invoice Date"
                register={register}
                registerName="invoiceDate"
              />
              <DateTimeInput
                label="Due Date"
                register={register}
                registerName="dueDate"
              />
              <TextInput
                label="Reference"
                register={register}
                registerName="reference"
                placeholder="Invoice reference"
                disabled={true}
              />
              <TextInput
                label="Amount Paid"
                register={register}
                registerName="amountPaid"
                type="number"
                placeholder="Enter amount paid"
              />
              <TextInput
                label="Sub Total"
                register={register}
                registerName="subTotal"
                type="number"
                placeholder="Enter total amount"
              />
              <TextInput
                label="Invoice Total"
                register={register}
                registerName="invoiceTotal"
                type="number"
                placeholder="Enter invoice total"
              />
            </div>
            <ButtonInput
              isSubmit={false}
              onClick={setTotalToItemTotal}
              label="Set Total to Item Total"
              classes="mt-4 float-right"
              disabled={itemTotal === invoiceTotal}
            />
          </MultiModalSection>

          <MultiModalSection classes="sm:max-w-3xl flex-grow" title="Invoice Items">
            <>
              {isEmpty ? (
                <p className="mt-8 text-center text-xl text-gray-700">No items attached to this invoice</p>
              ) :
                <InvoiceItemsTable
                  data={invoice?.items ?? []}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                />
              }
              <div className="">
                <ButtonInput
                  isSubmit={false}
                  onClick={() => setInvoiceItemOpen(true)}
                  label="Add Item"
                  classes="mt-4 float-right"
                />
              </div>
            </>
          </MultiModalSection>
        </div>

        <MultiModalSection classes="sm:max-w-3xl" title="Account Details">
          <div className="flex flex-col w-full gap-4">
            <BasketInvoiceInputs
              register={register}
              setValue={setValue}
              watch={watch}
              setSelectedAccount={setSelectedAccount}
              selectedAccount={selectedAccount}
              disabled={invoice?.status == InvoiceStatus.Paid}
            />

            <div className="flex justify-between items-center">
              <ButtonInput
                isSubmit={false}
                onClick={() => setOpen(false)}
                label="Close"
                classes="!bg-gray-200 hover:!bg-gray-300 !text-gray-800"
              />

              <div className="flex gap-4">
                {invoice?.status != InvoiceStatus.Credit && (
                  <ButtonInput
                    isSubmit={false}
                    onClick={() => setCreditModalOpen(true)}
                    label="Credit Invoice"
                    classes="bg-red-600 hover:bg-red-700 text-white"
                  />
                )}

                <ButtonInput
                  isSubmit={false}
                  onClick={updateInvoiceValues}
                  label="Update Invoice"
                  classes=""
                />
              </div>
            </div>
          </div>
        </MultiModalSection>

      </MultiModal>
    </>
  );
}